import { AlertTriangle, Bot, Check, Clock3, FileQuestion, Save, UserRound, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ArtifactViewer, WorkspaceNotice, WorkspaceUnavailable } from "../components/ProductWorkspaceView";
import { PageHeading } from "../components/PageHeading";
import { useCapabilities } from "../context/CapabilitiesContext";
import { usePortal } from "../context/PortalContext";
import { api } from "../lib/api";
import { handlePressedButtonGroupKey } from "../lib/buttonGroup";
import type { WorkbenchDecision, WorkbenchEvent } from "../types";

const DECISIONS: { id: WorkbenchDecision; label: string; icon: typeof Check }[] = [
  { id: "accept", label: "接受", icon: Check },
  { id: "reject", label: "拒绝", icon: X },
  { id: "defer", label: "延期", icon: Clock3 },
  { id: "needs_more_evidence", label: "需更多证据", icon: FileQuestion },
];

export function AiReviewPage() {
  const { track } = usePortal();
  const capabilities = useCapabilities();
  const workspace = track?.product_workspace;
  const review = workspace?.reviews[0];
  const [reviewerId, setReviewerId] = useState("codex");
  const [events, setEvents] = useState<WorkbenchEvent[]>([]);
  const [drafts, setDrafts] = useState<Record<string, { decision: WorkbenchDecision; reason: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const artifact = workspace?.artifacts.find((item) => item.id === review?.target_artifact_id);
  const findings = useMemo(() => review?.findings.filter((item) => item.reviewer_id === reviewerId) || [], [review, reviewerId]);
  const latest = useMemo(() => new Map(events.map((event) => [event.finding_id, event])), [events]);
  const reviewStats = useMemo(() => {
    const baseline = review?.comparison_baseline;
    const complete = baseline?.status === "complete";
    const expected = new Set(baseline?.expected_findings.map((item) => item.ground_truth_issue_id) || []);
    const confirmed = (reviewerId: string) => new Set((review?.findings || [])
      .filter((item) => item.reviewer_id === reviewerId && item.comparison?.adjudication === "confirmed" && item.comparison.ground_truth_issue_id)
      .map((item) => item.comparison!.ground_truth_issue_id!));
    const falsePositives = (reviewerId: string) => (review?.findings || [])
      .filter((item) => item.reviewer_id === reviewerId && item.comparison?.adjudication === "false_positive").length;
    const codex = confirmed("codex");
    const claude = confirmed("claude");
    const shared = new Set([...codex].filter((id) => claude.has(id)));
    const found = new Set([...codex, ...claude]);
    return {
      baselineStatus: !baseline ? "无比较基准" : complete ? "完整基准" : "基准不完整",
      codex: complete ? String(codex.size) : "未测量",
      claude: complete ? String(claude.size) : "未测量",
      shared: complete ? String(shared.size) : "不可计算",
      falsePositives: complete ? `${falsePositives("codex")} / ${falsePositives("claude")}` : "不可计算",
      misses: complete ? `${expected.size - codex.size} / ${expected.size - claude.size}` : "不可计算",
      sharedBlindspots: complete ? String([...expected].filter((id) => !found.has(id)).length) : "不可计算",
      adjudicated: new Set(events.map((item) => item.finding_id)).size,
    };
  }, [review, events]);

  useEffect(() => {
    if (!track || !workspace) return;
    let active = true;
    api.workbenchEvents(track.slug).then((items) => { if (active) setEvents(items); }).catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : String(reason)); });
    return () => { active = false; };
  }, [track?.slug, workspace]);

  async function saveDecision(findingId: string) {
    const draft = drafts[findingId];
    if (!track || !draft?.reason.trim()) { setError("保存前必须填写决策理由。"); return; }
    setSaving(findingId); setError(null);
    try {
      const event = await api.decideFinding(track.slug, findingId, draft.decision, draft.reason);
      setEvents((items) => [...items, event]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally { setSaving(null); }
  }

  if (!track) return <div className="loading-state">正在读取 AI 审查台</div>;
  return <>
    <PageHeading number="13" title="AI 审查台" description="并排查看作者版本、Codex 与 Claude 发现；每条意见必须由学习者接受、拒绝、延期或要求补证。" />
    {!workspace ? <WorkspaceUnavailable /> : <>
      <WorkspaceNotice workspace={workspace} />
      {!review ? <WorkspaceUnavailable reason="当前工作区没有审查任务。" /> : <>
        <section className="review-stats" aria-label="审查统计"><div><strong>{reviewStats.codex}</strong><span>Codex 发现</span></div><div><strong>{reviewStats.claude}</strong><span>Claude 发现</span></div><div><strong>{reviewStats.shared}</strong><span>共同发现</span></div><div><strong>{reviewStats.falsePositives}</strong><span>误报 Codex / Claude</span></div><div><strong>{reviewStats.misses}</strong><span>漏报 Codex / Claude</span></div><div><strong>{reviewStats.sharedBlindspots}</strong><span>共同盲点</span></div><div><strong>{reviewStats.adjudicated}</strong><span>已裁决</span></div><p><b>{reviewStats.baselineStatus}</b> · AI 一致不等于确定性验证；裁决仍需证据、修复和复验。</p></section>
        <div className="review-workbench">
        <section className="review-source"><div className="workbench-section-heading"><div><h2>{review.author_label}</h2></div></div><ArtifactViewer artifact={artifact} /></section>
        <section className="review-findings">
          <div className="reviewer-tabs" role="group" aria-label="审查者">{review.reviewers.map((reviewer) => <button type="button" data-group-option aria-pressed={reviewerId === reviewer.id} className={reviewerId === reviewer.id ? "is-selected" : ""} key={reviewer.id} onClick={() => setReviewerId(reviewer.id)} onKeyDown={(event) => handlePressedButtonGroupKey(event, review.reviewers.map((item) => item.id), reviewerId, setReviewerId)}>{reviewer.kind === "author" ? <UserRound size={16} /> : <Bot size={16} />}{reviewer.label}<span>{review.findings.filter((item) => item.reviewer_id === reviewer.id).length}</span></button>)}</div>
          {error && <p className="form-error" role="alert"><AlertTriangle size={16} />{error}</p>}
          {!capabilities.practice.writable && <div className="readonly-note">只读模式可查看审查意见；服务端拒绝写入决策。</div>}
          <div className="finding-list">{findings.length ? findings.map((finding) => {
            const previous = latest.get(finding.id);
            const draft = drafts[finding.id] || { decision: "needs_more_evidence" as WorkbenchDecision, reason: "" };
            return <article className={`finding-row severity-${finding.severity}`} key={finding.id}>
              <header><span>{finding.domain}</span><b>{finding.severity}</b><small>{finding.location}</small></header>
              <h3>{finding.impact}</h3>
              <dl><div><dt>复现</dt><dd>{finding.reproduction}</dd></div><div><dt>建议</dt><dd>{finding.suggestion}</dd></div><div><dt>证据</dt><dd>{finding.evidence}</dd></div></dl>
              {finding.resolution_links && <div className="resolution-links"><strong>修复与复验关联</strong><span>修复：{workspace.artifacts.find((item) => item.id === finding.resolution_links?.fix_artifact_id)?.title}</span><span>测试：{workspace.artifacts.find((item) => item.id === finding.resolution_links?.test_artifact_id)?.title}</span><p>{finding.resolution_links.reverification}</p></div>}
              {previous && <div className="previous-decision"><strong>最近决策：{DECISIONS.find((item) => item.id === previous.decision)?.label}</strong><span>{previous.reason}</span><time>{previous.occurred_at}</time></div>}
              <div className="decision-control"><div className="segmented-control">{DECISIONS.map(({ id, label, icon: Icon }) => <button type="button" key={id} className={draft.decision === id ? "is-selected" : ""} onClick={() => setDrafts((all) => ({ ...all, [finding.id]: { ...draft, decision: id } }))}><Icon size={14} />{label}</button>)}</div><label>理由<textarea rows={3} value={draft.reason} onChange={(event) => setDrafts((all) => ({ ...all, [finding.id]: { ...draft, reason: event.target.value } }))} /></label><button className="command-button" type="button" disabled={!capabilities.practice.writable || saving === finding.id} onClick={() => void saveDecision(finding.id)}><Save size={15} />{saving === finding.id ? "保存中" : "追加决策"}</button></div>
            </article>;
          }) : <WorkspaceUnavailable reason="该审查者暂未提交发现。" />}</div>
        </section>
        </div>
      </>}
    </>}
  </>;
}
